import { useEffect, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Modal from '../../../shared/components/Modal';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { getDistinctSections } from '../../Student/services/studentService';
import { TEACHER_LOGIN_DOMAIN, USERNAME_PATTERN } from '../../../shared/constant/teacherLogin';

// `canManageLogin`: only super admins set usernames and passwords (the manage-teacher Edge Function
// checks too).
function TeacherFormModal({ teacher, canManageLogin, onClose, onSubmit }) {
  const [firstname, setFirstname] = useState(teacher?.firstname ?? '');
  const [lastname, setLastname] = useState(teacher?.lastname ?? '');
  const [middlename, setMiddlename] = useState(teacher?.middlename ?? '');
  // The teacher's username or employee ID, which they sign in with.
  const [username, setUsername] = useState(teacher?.username ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const isNew = !teacher;
  const hasLogin = Boolean(teacher?.user_id);
  // A teacher from before usernames still signs in with their real email until they're given one.
  const legacyEmail =
    teacher?.email && !teacher.email.endsWith(`@${TEACHER_LOGIN_DOMAIN}`) ? teacher.email : null;
  const [advisoryGrade, setAdvisoryGrade] = useState(teacher?.advisory?.grade_level ?? '');
  const [advisorySection, setAdvisorySection] = useState(teacher?.advisory?.section ?? '');
  const [gradeSections, setGradeSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!advisoryGrade) {
      setGradeSections([]);
      return;
    }
    getDistinctSections(advisoryGrade)
      .then(setGradeSections)
      .catch(() => setGradeSections([]));
  }, [advisoryGrade]);

  // The current section stays listed even if no student is in it anymore.
  const sectionOptions = [...new Set([...gradeSections, ...(advisorySection ? [advisorySection] : [])])].sort();

  const handleAdvisoryGradeChange = (value) => {
    setAdvisoryGrade(value);
    setAdvisorySection('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isNew && !password) {
      setError('Set a password for the teacher to sign in with.');
      return;
    }

    if (password && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    const login = username.trim();
    const usernameChanged = canManageLogin && login !== (teacher?.username ?? '');

    if (isNew && !login) {
      setError('Set a username or employee ID for the teacher to sign in with.');
      return;
    }

    if (usernameChanged && !USERNAME_PATTERN.test(login)) {
      setError('Usernames are 3-50 letters, numbers, dots, dashes or underscores.');
      return;
    }

    if (password && !hasLogin && !login && !legacyEmail) {
      setError('Set a username or employee ID for the teacher to sign in with.');
      return;
    }

    if (advisoryGrade && !advisorySection) {
      setError('Select the advisory section, or set the advisory grade level to None.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        middlename: middlename.trim() || null,
        username: usernameChanged ? login : undefined,
        password: password || undefined,
        advisory: advisoryGrade ? { grade_level: advisoryGrade, section: advisorySection } : null,
      });
    } catch (err) {
      setError(err.message || 'Failed to save teacher');
      setSaving(false);
    }
  };

  return (
    <Modal title={teacher ? 'Edit Teacher' : 'Add Teacher'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="firstname" className="text-sm font-medium text-gray-700">First Name</label>
            <input
              id="firstname"
              type="text"
              required
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="lastname" className="text-sm font-medium text-gray-700">Last Name</label>
            <input
              id="lastname"
              type="text"
              required
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="middlename" className="text-sm font-medium text-gray-700">Middle Name</label>
          <input
            id="middlename"
            type="text"
            value={middlename}
            onChange={(e) => setMiddlename(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="username" className="text-sm font-medium text-gray-700">Username / Employee ID</label>
          <input
            id="username"
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            required={isNew}
            readOnly={!canManageLogin}
            value={username}
            onChange={(e) => setUsername(e.target.value.trim())}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] read-only:bg-gray-50 read-only:text-gray-500"
          />
          {legacyEmail && !teacher?.username && (
            <p className="text-xs text-gray-500">
              Signs in with {legacyEmail} for now. Give them a username to switch.
            </p>
          )}
        </div>

        {canManageLogin && (
        <div className="space-y-1">
          <label htmlFor="password" className="text-sm font-medium text-gray-700">
            {hasLogin ? 'New Password' : 'Password'}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required={isNew}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {isNew
              ? 'The teacher signs in with this username and password.'
              : hasLogin
                ? 'Leave blank to keep the current password.'
                : "This teacher has no login yet. Set a password to create one."}
          </p>
        </div>
        )}

        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-700">Advisory Class</p>
          <p className="text-xs text-gray-500">
            The section this teacher advises. An adviser can view their advisees' grades and attendance in every subject.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={advisoryGrade}
              onChange={(e) => handleAdvisoryGradeChange(e.target.value)}
              aria-label="Advisory grade level"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">None</option>
              {GRADE_LEVELS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>

            <select
              value={advisorySection}
              onChange={(e) => setAdvisorySection(e.target.value)}
              disabled={!advisoryGrade}
              aria-label="Advisory section"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0] disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">Select section</option>
              {sectionOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default TeacherFormModal;
